import { useState, useEffect, useRef, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { animate, svg } from 'animejs';

const tierColor = '#B8C0CC'; // silver/steel — cool metallic tone
const shimmerPeak = '#FFFFFF'; // bright shimmer highlight

// ─── Shared castle structure + plaque ────────────────────────────────────
const rookPathsAt = (dx: number) => [
  `M${14 + dx} 8 V2 H${22 + dx} V8 H${27 + dx} V2 H${37 + dx} V8 H${42 + dx} V2 H${50 + dx} V8`,
  `M${12 + dx} 8 H${52 + dx} V16 H${12 + dx} Z`,
  `M${16 + dx} 16 L${20 + dx} 30 H${44 + dx} L${48 + dx} 16`,
  `M${18 + dx} 30 H${46 + dx} V52 H${18 + dx} Z`,
  `M${18 + dx} 40 H${46 + dx}`,
  `M${18 + dx} 52 L${12 + dx} 60 H${52 + dx} L${46 + dx} 52`,
];

const BATTLEMENT_PATH = (() => {
  let d = `M46 30 L46 22`;
  for (let i = 0; i < 21; i++) {
    const x = 46 + i * 8;
    d += ` L${x} 22 L${x + 4} 22 L${x + 4} 30`;
  }
  return d;
})();

// Ornamental plaque — framed inscription panel centered on the wall
const PLAQUE_PATHS = [
  // Outer frame
  `M56 33 H200 V57 H56 Z`,
  // Inner frame
  `M58 35 H198 V55 H58 Z`,
  // Corner flourishes
  `M56 33 L53 30 M56 57 L53 60 M200 33 L203 30 M200 57 L203 60`,
];

const STRUCTURE = [
  ...rookPathsAt(0),
  ...rookPathsAt(192),
  BATTLEMENT_PATH,
  `M46 30 H210 V60 H46 Z`,
  `M46 40 H210`,
  `M46 50 H210`,
  `M8 60 H248 V68 H8 Z`,
  `M4 68 H252 V74 H4 Z`,
];
const BRICK_ROWS = [`M46 35 H210`, `M46 45 H210`, `M46 55 H210`, `M8 64 H248`, `M4 71 H252`];
const BRICK_TICKS: string[] = [];
for (let row = 0; row < 4; row++) {
  const yTop = [31, 41, 51, 62][row];
  const yBot = [35, 45, 55, 66][row];
  const offset = row % 2 === 0 ? 0 : 6;
  for (let x = 54 + offset; x < 210; x += 12) {
    BRICK_TICKS.push(`M${x} ${yTop} V${yBot}`);
  }
}

// All paths traced by anime.js — structure + plaque
const ALL_TRACE_PATHS = [...STRUCTURE, ...PLAQUE_PATHS];

// ─── Castle SVG background with plaque + glowing pulse ──────────────────
function CastleBackground({ uid }: { uid: string }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pulseId = `${uid}-pulse`;
  const glowFilterId = `${uid}-glow-filter`;
  const [phase, setPhase] = useState<'tracing' | 'done'>('tracing');

  useEffect(() => {
    if (!svgRef.current) return;
    const cutPaths = svgRef.current.querySelectorAll(`#${uid}-cutLayer path`);
    if (cutPaths.length === 0) return;
    const drawables = svg.createDrawable(cutPaths);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2200,
      delay: (_el: any, i: number) => i * 50,
      onComplete: () => {
        const cut = svgRef.current?.querySelector(`#${uid}-cut`);
        if (cut) {
          animate(cut, {
            opacity: [1, 0],
            duration: 700,
            onComplete: () => setTimeout(() => setPhase('done'), 100),
          });
        }
      },
    });
  }, [uid]);

  return (
    <svg ref={svgRef} viewBox="0 0 256 80" width="480" height="150"
      preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={tierColor} stopOpacity="0.05" />
          <stop offset="100%" stopColor={tierColor} stopOpacity="0" />
        </radialGradient>

        {/* Bright neon-matching pulse gradient */}
        <motion.linearGradient id={pulseId} gradientUnits="userSpaceOnUse"
          animate={{ x1: [-40, 296], x2: [-20, 316], y1: [-10, 90], y2: [0, 100] }}
          transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}>
          <stop stopColor={tierColor} stopOpacity="0" />
          <stop offset="0.4" stopColor={tierColor} stopOpacity="1" />
          <stop offset="0.5" stopColor={shimmerPeak} stopOpacity="1" />
          <stop offset="0.6" stopColor={tierColor} stopOpacity="1" />
          <stop offset="1" stopColor={tierColor} stopOpacity="0" />
        </motion.linearGradient>

        {/* Neon glow filter — applied to the pulse stroke so it shimmers like the text */}
        <filter id={glowFilterId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect width="256" height="80" fill={`url(#${uid}-glow)`} />

      {/* Cobblestone texture */}
      <g stroke={tierColor} strokeWidth="0.4" fill="none" opacity="0.08">
        {BRICK_ROWS.map((d, i) => <path key={i} d={d} />)}
      </g>
      <g stroke={tierColor} strokeWidth="0.3" fill="none" opacity="0.06">
        {BRICK_TICKS.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* Base castle strokes — matches plaque edge opacity */}
      <g stroke={tierColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        fill="none" opacity="0.5">
        {STRUCTURE.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* OPAQUE plaque backdrop — drawn AFTER castle strokes so lines don't bleed through */}
      <rect x="56" y="33" width="144" height="24" fill="#0d1117" />

      {/* Plaque frame on top of backdrop — same opacity as castle */}
      <g stroke={tierColor} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
        fill="none" opacity="0.5">
        {PLAQUE_PATHS.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* Bright pulse overlay — glowing scan */}
      {phase === 'done' && (
        <g filter={`url(#${glowFilterId})`}>
          <g strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"
            stroke={`url(#${pulseId})`}>
            {ALL_TRACE_PATHS.map((d, i) => <path key={i} d={d} />)}
          </g>
        </g>
      )}

      <g id={`${uid}-cut`}>
        <rect width="256" height="80" fill="#0d1117" opacity="0.98" />
        <g id={`${uid}-cutLayer`} stroke={tierColor} strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" fill="none">
          {ALL_TRACE_PATHS.map((d, i) => <path key={i} d={d} />)}
        </g>
      </g>
    </svg>
  );
}

function TitleFrame({ children, uid }: { children: ReactNode; uid: string }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 480, height: 150 }}>
      <div className="absolute inset-0">
        <CastleBackground uid={uid} />
      </div>
      {/* Plaque center sits ~9px below container center in rendered SVG — shift text down to match */}
      <div className="relative z-10 flex items-center justify-center" style={{ marginTop: 18 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Neon-glow title: bright tier-color with heavy glow ─────────────────
interface NeonTitleProps {
  fontFamily: string;
  fontSize?: number;
  letterSpacing?: string;
  fontWeight?: number | string;
}
function NeonTitle({
  fontFamily, fontSize = 36, letterSpacing = '0.1em', fontWeight = 900,
}: NeonTitleProps) {
  return (
    <h1 className="leading-none text-white"
      style={{
        fontFamily,
        fontSize,
        fontWeight,
        letterSpacing,
        textShadow: [
          `0 0 4px ${tierColor}`,
          `0 0 10px ${tierColor}`,
          `0 0 20px ${tierColor}CC`,
          `0 0 34px ${tierColor}88`,
          `0 2px 6px rgba(0,0,0,0.9)`,
        ].join(', '),
      }}>
      SIEGE
    </h1>
  );
}

// ─── Variations ─────────────────────────────────────────────────────────
function VariantMetalMania() {
  const uid = 'mm-' + Math.random().toString(36).slice(2, 7);
  return (
    <TitleFrame uid={uid}>
      <NeonTitle fontFamily="'Metal Mania', system-ui" fontSize={42} letterSpacing="0.06em" fontWeight={400} />
    </TitleFrame>
  );
}

function VariantMetamorphous() {
  const uid = 'mt-' + Math.random().toString(36).slice(2, 7);
  return (
    <TitleFrame uid={uid}>
      <NeonTitle fontFamily="'Metamorphous', serif" fontSize={34} letterSpacing="0.14em" fontWeight={400} />
    </TitleFrame>
  );
}

// ─── Preview Page ────────────────────────────────────────────────────────
export function SiegeTitleIntegrationPreview() {
  const variations = [
    {
      title: 'A — METAL MANIA / PLAQUE',
      subtitle: 'Grunge brush font on the inscription plaque. Neon tier-color glow. Castle shimmer matches letter intensity.',
      Component: VariantMetalMania,
    },
    {
      title: 'B — METAMORPHOUS / PLAQUE',
      subtitle: 'Tall angular modern-medieval font on the plaque. Same neon glow and matched castle shimmer.',
      Component: VariantMetamorphous,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0d1117] text-white py-12 px-8">
      <h1 className="text-center text-sm font-mono uppercase tracking-widest text-white/40 mb-12">
        SIEGE — Neon Plaque + Matched Shimmer
      </h1>
      <div className="max-w-4xl mx-auto space-y-10">
        {variations.map(({ title, subtitle, Component }, i) => (
          <div key={i} className="border border-white/10 rounded-lg p-6 bg-white/[0.02]">
            <div className="text-xs font-mono uppercase tracking-widest text-white/60 mb-1">
              {title}
            </div>
            <div className="text-xs text-white/40 mb-6">{subtitle}</div>
            <div className="flex flex-col items-center">
              <Component />
              <div className="mt-3 flex gap-3 text-[10px] font-mono uppercase tracking-widest text-white/40">
                {'STRATEGICALLY INTEGRATED EQUITY GROWTH ENGINE'.split(' ').map((w, j) => <span key={j}>{w}</span>)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
